/* eslint-disable no-param-reassign */
/* eslint-disable no-plusplus */
import { Bar, XAxis, Tooltip, ReferenceLine, ResponsiveContainer, ComposedChart, Area } from "recharts";
import { useCallback, useMemo, useState } from "react";
import moment from "moment";
import currency from "currency.js";
import Collapse from "antd/es/collapse/Collapse";
import { BACKGROUND_COLORS, BORDER_COLORS, COLORS, TEXT_COLORS } from "../../../constants/design/colors";
import { IAevoTransaction } from "../../../interfaces/aevo";
import { TooltipWrapper } from "./style";
import { TX_TYPES } from "../tables/TxHistoryTable";
import { useMarkets } from "../../../hooks/api/aevo/useMarkets";
import { TitleWrapper } from "../../Table/style";

interface IData {
  inflow: number;
  outflow: number;
  cumulative: number;
  date: string;
}

interface ITradeHistoryProps {
  data: IAevoTransaction[];
  startDate: moment.Moment;
  endDate: moment.Moment;
}

const dataKeyColor = {
  inflow: COLORS.green.one,
  outflow: COLORS.red.one,
  net: COLORS.highlight.one,
};

function TxHistoryChart({ data, startDate, endDate }: ITradeHistoryProps) {
  const [isShow, setShow] = useState<boolean>(window.localStorage.getItem("showTxHistoryChart") === "true");
  const { data: marketsData } = useMarkets();

  const computeUSDValue = useCallback(
    (amount: string, asset: string) => {
      let collateral = asset;

      if (asset === "WETH") {
        collateral = "ETH";
      } else if (asset === "WBTC") {
        collateral = "BTC";
      }

      if (collateral && !collateral.includes("USD")) {
        const market = marketsData?.find((m) => m.underlying_asset === collateral);

        if (market) {
          return Number(market.mark_price) * Number(amount);
        }
      } else {
        return Number(amount);
      }

      return 0;
    },
    [marketsData]
  );

  const sanitizedData = useMemo(() => {
    const results: IData[] = [];

    // Populate the results with days (format: D/M) from startDate to endDate
    const difference = endDate.diff(startDate, "days") + 1;

    for (let i = 0; i < difference; i++) {
      const date = startDate.clone().add(i, "days").format("D/M");
      results.push({ inflow: 0, outflow: 0, cumulative: 0, date });
    }

    if (data.length === 0) return results;

    data
      .filter((d) => TX_TYPES[d.tx_type] !== undefined)
      .forEach((item) => {
        const { tx_type, amount, decimals, finalized_timestamp, collateral_name } = item;
        const date = moment(Number(finalized_timestamp) / 1e6).format("D/M");
        const index = results.findIndex((result) => result.date === date);

        if (results[index]) {
          const parsedAmount = computeUSDValue(String(parseFloat(amount) / 10 ** decimals), collateral_name);
          if (TX_TYPES[tx_type][1]) {
            results[index].inflow += Number(parsedAmount) || 0;
          } else {
            results[index].outflow -= Number(parsedAmount) || 0;
          }
        }
      });

    // Update cumulative volume after processing all data
    results.reduce((acc, result) => {
      result.cumulative = acc + result.inflow + result.outflow;
      return result.cumulative;
    }, 0);

    return results;
  }, [computeUSDValue, data, endDate, startDate]);

  const onSet = useCallback(() => {
    window.localStorage.setItem("showTxHistoryChart", JSON.stringify(!isShow));
    setShow(!isShow);
  }, [isShow]);

  return (
    <div>
      <Collapse
        expandIconPosition="end"
        destroyInactivePanel
        items={[
          {
            key: "1",
            label: <TitleWrapper>Cumulative Chart</TitleWrapper>,
            children: (
              <ResponsiveContainer
                style={{
                  background:
                    "url(data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAUAAAAFCAYAAACNbyblAAAAAXNSR0IArs4c6QAAABlJREFUGFdjZGBg+M+ABhgJCcIVEFQJNxkAVpAGASEHn7kAAAAASUVORK5CYII=)",
                  backgroundColor: BACKGROUND_COLORS.two,
                }}
                width="100%"
                height={300}
              >
                <ComposedChart width={1200} height={300} data={sanitizedData} stackOffset="sign">
                  <XAxis dataKey="date" color={TEXT_COLORS.one} fontSize={10} />
                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <TooltipWrapper>
                            <div>{moment(label, "D/M").format("Do MMM")}</div>
                            {payload.map((item) => (
                              <div
                                key={item.name}
                                style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gridGap: 8 }}
                              >
                                <span style={{ color: (dataKeyColor as any)[(item as any).dataKey] }}>{item.name}</span>
                                <span
                                  style={{
                                    textAlign: "right",
                                    color: item.value === 0 ? TEXT_COLORS.two : TEXT_COLORS.one,
                                  }}
                                >
                                  {currency(Number(item.value)).format()}
                                </span>
                              </div>
                            ))}
                          </TooltipWrapper>
                        );
                      }

                      return null;
                    }}
                    formatter={(value, name) => [currency(Number(value || 0)).format(), name]}
                  />
                  <ReferenceLine y={0} stroke={BORDER_COLORS.one} />
                  <Bar barSize={4} stackId="trades" dataKey="inflow" stroke={COLORS.green.one} />
                  <Bar barSize={4} stackId="trades" dataKey="outflow" stroke={COLORS.red.one} />
                  <Area
                    type="monotone"
                    dataKey="cumulative"
                    startOffset={"silhouette"}
                    stroke={COLORS[sanitizedData[sanitizedData.length - 1]?.cumulative > 0 ? "green" : "red"].three}
                    fill={COLORS[sanitizedData[sanitizedData.length - 1]?.cumulative > 0 ? "green" : "red"].four}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            ),
          },
        ]}
      />
    </div>
  );
}

export default TxHistoryChart;
